// src/lib/wallet/livello.ts
import type { Wallet } from '@/types/qbank';

/**
 * Il «livello del giocatore» e` il livello della sua moneta EXP — mai quello di un Elo.
 *
 * 🔴 Il difetto che questo file chiude (misurato il 2026-10-03 12:25Z su `stg-tma` `1219-4f92919`, profili b e c
 * [M consegna-tornei-orcb]): `GET /v1/me/wallet` portava UNA moneta, «Elo 100 backgammon-1pt-blitz», code
 * `ELO-100-BACKGAMMON-1PT-BLITZ`, `model` 1, `level` 8 (dalla simulazione MCP di gionko), e la testata mostrava il
 * bollino «8»: il livello Elo del backgammon letto come livello del giocatore. Il difetto era GIA' su `main`:
 * `LevelBadge` e `PlayerIdentity` prendevano `find(w => w.level != null)`, cioe' il PRIMO wallet con un livello, e
 * `RankedPanel` ripiegava sull'unica moneta con livello. Funzionava per caso finche' l'EXP era l'unica moneta Exp.
 *
 * Perche' succede (ADR-0008 §2-3): ogni Elo e` una moneta `model=1` (Exp) con chiave `elo-<gioco>-<modalita'>`, cosi'
 * da poter fare da `referenceCurrencyId` di una lega. Convive quindi con l'EXP del giocatore, e per il wallet ha la
 * stessa forma: un `level` non nullo. ADR-0038 (stella della testata) vuole in quella stella la moneta EXP del
 * giocatore, che «si nasconde da sola» quando non c'e'.
 *
 * ⚠️ Il riconoscimento e` sul `code` (la forma `Wallet` non porta `model`, e un Elo e` `model=1` come l'EXP): comincia
 * con «ELO-», senza distinguere le maiuscole. `code` e` mutabile (una rinomina lo cambia senza toccare i wallet), ma
 * la chiave `elo-…` e` il contratto di ADR-0008 §2 e un Elo nuovo nasce cosi'.
 */
const PREFISSO_ELO = 'elo-';

/**
 * Il code della moneta di rating di QDuels, cioe' dell'EXP del giocatore sul QBank VECCHIO (quello che la TMA legge in
 * produzione): QRAT e` insieme «the wallet carrying the Elo and its level» (`RankedPanel`) e «the EXP / CR-Elo wallet»
 * (`CurrencyHud`). Non comincia con «ELO-»: non e` un Elo nel senso di ADR-0008.
 */
export const RATING_COIN_CODE = 'QRAT';

/** Una moneta Elo (ADR-0008 §2: chiave `elo-<gioco>-<modalita'>`). */
export function isMonetaElo(w: Pick<Wallet, 'code'>): boolean {
  return (w.code ?? '').trim().toLowerCase().startsWith(PREFISSO_ELO);
}

/** Le monete che portano un livello e NON sono un Elo. */
export function monetaConLivelloNonElo(wallets: Wallet[] | undefined): Wallet[] {
  return (wallets ?? []).filter((w) => w.level != null && !isMonetaElo(w));
}

/**
 * L'UNICA moneta non Elo con un livello, o `undefined`. Con piu' di una NON si indovina: stessa regola del ripiego di
 * `RankedPanel` («se il code venisse rinominato mostriamo comunque un livello, ma non scegliamo mai fra due»).
 */
export function monetaUnicaConLivello(wallets: Wallet[] | undefined): Wallet | undefined {
  const candidate = monetaConLivelloNonElo(wallets);
  return candidate.length === 1 ? candidate[0] : undefined;
}

/**
 * LA REGOLA, UNA VOLTA SOLA, per `LevelBadge`, `PlayerIdentity` e `RankedPanel`: la moneta che porta il livello del
 * giocatore.
 *
 *   1. **QRAT con un livello**, se c'e'. Ha la precedenza e vale in QUALUNQUE posizione della lista: in produzione la
 *      moneta del livello E' QRAT, e chi ha QRAT piu' un'altra moneta Exp non Elo vede da sempre la stella — ne'
 *      l'ordine ne' la presenza di un'altra moneta la tolgono (revisione di #561, R1).
 *   2. altrimenti **l'unica moneta non Elo con un livello** (su staging, dove non c'e' QRAT, l'EXP);
 *   3. altrimenti **nessuna**: nessun wallet, solo Elo, nessun livello, oppure PIU' monete non Elo con livello senza
 *      QRAT (non si indovina).
 * Un Elo non e` mai candidato, ne' al punto 1 ne' al 2.
 */
export function monetaDelLivelloOf(wallets: Wallet[] | undefined): Wallet | undefined {
  const qrat = (wallets ?? []).find(
    (w) => w.level != null && (w.code ?? '').trim().toUpperCase() === RATING_COIN_CODE,
  );
  return qrat ?? monetaUnicaConLivello(wallets);
}

/**
 * Il livello del giocatore, o `null` quando non c'e' da mostrare. Il chiamante nasconde, non stampa zero.
 */
export function livelloGiocatoreOf(wallets: Wallet[] | undefined): number | null {
  return monetaDelLivelloOf(wallets)?.level ?? null;
}
